import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatErr } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { ShieldAlert } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Build an authenticated <img> src for a stored evidence photo.
function evidenceSrc(url) {
  if (!url) return "";
  const base = /^https?:\/\//.test(url) ? url : `${BACKEND_URL}${url}`;
  const token = localStorage.getItem("vb_access_token");
  return token ? `${base}?auth=${encodeURIComponent(token)}` : base;
}

const STATUS = {
  open: { label: "open", cls: "err" },
  escalated: { label: "escalated", cls: "warn" },
  rework: { label: "rework", cls: "warn" },
  resubmitted: { label: "resubmitted", cls: "" },
  closed: { label: "closed", cls: "ok" },
};

const HIST_LABEL = {
  rejected: "Sent back for redo",
  reworked: "Reworked by operator",
  escalated: "Escalated to FSMS Mgmt Rep",
  accepted: "Accepted & closed",
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.open;
  return <span className={`vb-pill ${s.cls}`}>{s.label}</span>;
}

export default function Deviations() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [active, setActive] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [verdict, setVerdict] = useState("accepted");
  const [comments, setComments] = useState("");
  const [preventive, setPreventive] = useState("");
  const [err, setErr] = useState("");
  const { activeSiteId } = useSite();

  const load = async () => {
    const url = statusFilter ? `/deviations?status=${statusFilter}` : "/deviations";
    const { data } = await api.get(url);
    setItems(data.deviations || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, activeSiteId]);

  const open = (d) => { setActive(d); setComments(""); setPreventive(""); setVerdict("accepted"); setErr(""); };

  const canSubmit = !reviewBusy && (
    verdict === "accepted" ? true
      : verdict === "escalated" ? !!preventive.trim()
      : verdict === "rejected" ? !!comments.trim()
      : false
  );

  const submitReview = async () => {
    setReviewBusy(true); setErr("");
    try {
      await api.post(`/deviations/${active.id}/review`, { verdict, comments, preventive_action: preventive });
      setActive(null);
      load();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally { setReviewBusy(false); }
  };

  return (
    <div className="vb-fade-in" data-testid="deviations-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>CCP Deviations</h1>
          <p className="opacity-70">Review out-of-range readings, document root-cause analysis, and close with preventive actions.</p>
        </div>
        <select className="vb-input max-w-[200px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="dev-filter">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="escalated">Escalated</option>
          <option value="rework">In rework</option>
          <option value="resubmitted">Resubmitted</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      <div className="vb-card overflow-x-auto">
        <table className="vb-table">
          <thead><tr><th>When</th><th>CCP</th><th>Value</th><th>Range</th><th>By</th><th>Root cause</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No deviations 🎉</td></tr>}
            {items.map((d) => (
              <tr key={d.id} data-testid={`dev-row-${d.id}`}>
                <td>{new Date(d.logged_at).toLocaleString()}</td>
                <td><strong>{d.ccp_name}</strong></td>
                <td style={{ color: "var(--vb-error)" }}>{d.value}{d.unit}</td>
                <td className="opacity-70">{d.min_value}–{d.max_value}{d.unit}</td>
                <td>{d.logged_by_name}</td>
                <td className="opacity-80" style={{ maxWidth: 240 }}>{(d.root_cause || "").slice(0, 60)}{d.root_cause?.length > 60 ? "…" : ""}</td>
                <td><StatusPill status={d.status || "open"} /></td>
                <td><button className="vb-btn ghost text-sm" onClick={() => open(d)} data-testid={`dev-open-${d.id}`}>{(d.status || "open") === "closed" ? "View" : "Review"}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active && createPortal((
        <div className="vb-modal-bg" data-testid="dev-detail-modal">
          <div className="vb-modal" style={{ maxWidth: 760 }}>
            <h2 className="flex items-center gap-2 flex-wrap"><ShieldAlert size={18} className="inline" /> Deviation review — {active.ccp_name} <StatusPill status={active.status || "open"} /></h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
              <div className="vb-card p-3"><div className="opacity-60 text-xs">Value</div><strong style={{ color: "var(--vb-error)" }}>{active.value}{active.unit}</strong></div>
              <div className="vb-card p-3"><div className="opacity-60 text-xs">Acceptable range</div><strong>{active.min_value}–{active.max_value}{active.unit}</strong></div>
              <div className="vb-card p-3 sm:col-span-2"><div className="opacity-60 text-xs">Staff root cause</div>{active.root_cause || "—"}</div>
              <div className="vb-card p-3 sm:col-span-2"><div className="opacity-60 text-xs">Corrective action</div>{active.corrective_action || "—"}</div>
              {active.immediate_action_taken && <div className="vb-card p-3 sm:col-span-2"><div className="opacity-60 text-xs">Immediate action taken</div>{active.immediate_action_taken}</div>}
              {Array.isArray(active.photo_urls) && active.photo_urls.length > 0 && (
                <div className="vb-card p-3 sm:col-span-2" data-testid="dev-photos">
                  <div className="opacity-60 text-xs mb-2">Evidence photos ({active.photo_urls.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {active.photo_urls.map((p, i) => (
                      <a key={i} href={evidenceSrc(p.url)} target="_blank" rel="noreferrer"
                        data-testid={`dev-photo-${i}`}
                        style={{ width: 96, height: 96, borderRadius: 8, overflow: "hidden", border: "1px solid var(--vb-border)", display: "block" }}
                        title={p.uploaded_by_name ? `Uploaded by ${p.uploaded_by_name}` : "Evidence photo"}>
                        <img src={evidenceSrc(p.url)} alt={`Evidence ${i + 1}`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {Array.isArray(active.rework_history) && active.rework_history.length > 0 && (
              <div className="vb-card p-3 mb-4" data-testid="dev-history">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--vb-text-2)" }}>Workflow history</div>
                {active.rework_history.map((h, i) => (
                  <div key={`${h.action}-${h.at}-${i}`} className="text-sm pb-2 mb-2" style={{ borderBottom: i < active.rework_history.length - 1 ? "1px solid var(--vb-border)" : "none" }}>
                    <div><strong>{HIST_LABEL[h.action] || h.action}</strong> · {h.by_name} · <span className="opacity-60">{new Date(h.at).toLocaleString()}</span></div>
                    {h.comment && <div className="opacity-80">Redo note: {h.comment}</div>}
                    {h.corrective_action && <div className="opacity-80">Corrective: {h.corrective_action}</div>}
                    {h.notes && <div className="opacity-80">Notes: {h.notes}</div>}
                    {h.preventive_action && <div className="opacity-80">Preventive: {h.preventive_action}</div>}
                    {h.new_value != null && <div className="opacity-80">New reading: {h.new_value}{active.unit}</div>}
                  </div>
                ))}
              </div>
            )}

            {(active.status || "open") !== "closed" ? (
              <>
                <h3 className="vb-heading text-lg mb-2" style={{ color: "var(--vb-text-2)" }}>
                  {active.status === "escalated" ? "Close-out (FSMS Management Rep)" : active.status === "resubmitted" ? "Reconfirm rework" : "Manager close-out"}
                </h3>
                {active.status === "escalated" && (
                  <div className="vb-pill warn mb-3" data-testid="dev-escalated-note">Escalated — awaiting FSMS Management Representative to close.</div>
                )}
                <div className="mb-3">
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Verdict</label>
                  <select className="vb-input" value={verdict} onChange={(e) => setVerdict(e.target.value)} data-testid="review-verdict">
                    <option value="accepted">Accept corrective action</option>
                    {active.status !== "escalated" && <option value="escalated">Escalate to FSMS Mgmt Rep</option>}
                    <option value="rejected">Reject — send back for redo</option>
                  </select>
                </div>

                {verdict === "accepted" && (
                  <>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Preventive action (optional)</label>
                    <textarea className="vb-input mb-3" rows={2} value={preventive} onChange={(e) => setPreventive(e.target.value)} data-testid="review-preventive"
                      placeholder="What will prevent this from recurring? (optional)" />
                  </>
                )}
                {verdict === "escalated" && (
                  <>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Preventive action *</label>
                    <textarea className="vb-input mb-3" rows={2} value={preventive} onChange={(e) => setPreventive(e.target.value)} data-testid="review-preventive"
                      placeholder="Preventive action to accompany the escalation" />
                  </>
                )}
                {verdict === "rejected" && (
                  <>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>What needs to be redone *</label>
                    <textarea className="vb-input mb-3" rows={3} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="review-comments"
                      placeholder="Describe exactly what the operator must rework before resubmitting" />
                  </>
                )}

                {err && <div className="vb-pill err mb-3">{err}</div>}
                <div className="flex gap-3 justify-end">
                  <button className="vb-btn ghost text-sm" onClick={() => setActive(null)} data-testid="review-cancel">Cancel</button>
                  <button className="vb-btn text-sm" onClick={submitReview} disabled={!canSubmit} data-testid="review-submit">
                    {reviewBusy ? "Saving…"
                      : verdict === "accepted" ? "Close deviation"
                      : verdict === "escalated" ? "Escalate to FSMS Mgmt Rep"
                      : "Send back for redo"}
                  </button>
                </div>
              </>
            ) : (
              <div>
                <h3 className="vb-heading text-lg mb-2" style={{ color: "var(--vb-text-2)" }}>Closed by {active.manager_review?.reviewed_by_name}</h3>
                {active.manager_review?.preventive_action && <p className="text-sm">Preventive: {active.manager_review.preventive_action}</p>}
                <p className="text-xs opacity-60 mt-1">{active.manager_review?.reviewed_at ? new Date(active.manager_review.reviewed_at).toLocaleString() : ""}</p>
                <div className="text-right mt-4"><button className="vb-btn ghost text-sm" onClick={() => setActive(null)}>Close</button></div>
              </div>
            )}
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
